import { gsap } from 'gsap'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The `useHeaderAnimation` performs the show/hide animations
 * for the site header
 *
 * @param {object} ref - the element ref
 * @param {boolean} isActive - whether the header is active
 * @returns {boolean} whether is visible
 */
function useHeaderAnimation (ref, isActive) {
  const [isVisible, setIsVisible] = useState(false)
  const wasActive = useRef(false)
  const gEl = gsap.utils.selector(ref)

  /**
   * Show the header
   */
  const show = useCallback(() => {
    gsap.killTweensOf(ref.current)
    gsap.killTweensOf(gEl('a, button'))
    setIsVisible(true)

    gsap.set(ref.current, {
      y: '-50%',
      opacity: 0
    })

    gsap.set(gEl('a, button'), {
      opacity: 0,
      y: -20
    })

    gsap.to(ref.current, {
      y: '0%',
      opacity: 1,
      duration: 0.5,
      ease: 'cubic.out'
    })

    gsap.to(gEl('a, button'), {
      opacity: 1,
      y: 0,
      ease: 'cubic.out',
      stagger: {
        each: 0.1,
        from: 'start'
      },
      duration: 0.5
    })
  }, [])

  /**
   * Hide the header
   */
  const hide = useCallback(() => {
    gsap.killTweensOf(ref.current)
    gsap.killTweensOf(gEl('a, button'))

    gsap.to(ref.current, {
      y: '-50%',
      opacity: 0,
      duration: 0.2,
      ease: 'cubic.in',
      onComplete: () => {
        setIsVisible(false)
      }
    })

    gsap.to(gEl('a, button'), {
      opacity: 0,
      y: -20,
      ease: 'cubic.out',
      stagger: {
        each: 0.1,
        from: 'end'
      },
      duration: 0.5
    })
  }, [])

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    wasActive.current = false

    return () => {
      if (!ref.current) {
        return
      }

      gsap.killTweensOf(ref.current)
      gsap.killTweensOf(gEl('a, button'))
    }
  }, [])

  /**
   * Effect: When the `isHeaderActive` state changes
   */
  useEffect(() => {
    if (isActive === wasActive.current) {
      return
    }

    wasActive.current = isActive

    if (isActive) {
      show()

      return
    }

    hide()
  }, [isActive])

  return isVisible
}

// Export
export default useHeaderAnimation
